<template>
  <div class="category-profit-report">
    <v-card flat outlined class="report-table-card">
      <v-card-title class="report-table-header">
        <div>
          <div class="report-title">Sale By Product</div>
          <div class="report-subtitle">
            {{ Category.length }} groups found
          </div>
        </div>

        <v-spacer />

        <div class="summary-box">
          <div class="summary-label">Qty</div>
          <div class="summary-value">{{ totalReportQty }}</div>
        </div>

        <div class="summary-box">
          <div class="summary-label">Sale</div>
          <div class="summary-value">{{ totalReportSale | currency }}</div>
        </div>

        <div v-if="!hideCost" class="summary-box">
          <div class="summary-label">Cost</div>
          <div class="summary-value">{{ totalReportCost | currency }}</div>
        </div>

        <div v-if="!hideProfit" class="summary-box">
          <div class="summary-label">Profit</div>
          <div class="summary-value" :class="{ loss: totalProfit < 0 }">
            {{ totalProfit | currency }}
          </div>
        </div>
      </v-card-title>

      <v-divider />

      <v-card-text class="pa-0">
        <v-progress-linear v-if="isLoading" indeterminate height="3" />

        <v-data-table
          v-else
          dense
          :sort-by="sortBy"
          :mobile-breakpoint="0"
          :headers="visibleHeaders"
          :items="Category"
          :items-per-page="-1"
          :expanded.sync="expanded"
          show-expand
          hide-default-footer
          class="category-profit-table"
        >
          <template v-slot:item.data-table-expand="{ item, isExpanded, expand }">
            <v-btn class="no-print" icon small @click="expand(!isExpanded)">
              <v-icon v-if="isExpanded">mdi-chevron-up</v-icon>
              <v-icon v-else>mdi-chevron-down</v-icon>
            </v-btn>
          </template>

          <template v-slot:item.name="{ item }">
            <span class="item-name">{{ item.name || "N/A" }}</span>
          </template>

          <template v-slot:item.totalQty="{ item }">
            <span class="font-weight-bold">
              {{ item.totalQty }}
            </span>
            <span class="percent-text">
              {{ getQtyPercent(item) }}%
            </span>
          </template>

          <template v-slot:item.totalPrice="{ item }">
            <span class="money">
              {{ item.totalPrice | currency }}
            </span>
          </template>

          <template v-slot:item.totalCost="{ item }">
            <span class="money">
              {{ item.totalCost | currency }}
            </span>
          </template>

          <template v-slot:item.totalProfit="{ item }">
            <span class="money" :class="{ loss: Number(item.totalProfit || 0) < 0 }">
              {{ item.totalProfit | currency }}
            </span>

            <span class="profit-percent">
              {{ getProfitPercent(item) }}%
            </span>
          </template>

          <template v-slot:expanded-item="{ headers, item }">
            <td :colspan="headers.length" class="pa-0">
              <div class="expanded-panel">
                <Product
                  :key="`${groupBy || 'category'}-${item.id}`"
                  :CategoryId="groupBy === 'category' || !groupBy ? item.id : null"
                  :groupBy="groupBy"
                  :GroupId="item.id"
                  :hideProfit="hideProfit"
                  :hideCost="hideCost"
                  :start="start"
                  :end="end"
                  :CustomerId="CustomerId"
                  :serializedItem="serializedItem"
                />
              </div>
            </td>
          </template>

          <template v-slot:body.append v-if="Category.length > 0">
            <tr class="total-row">
              <td></td>
              <td class="font-weight-bold">Total</td>
              <td class="text-center font-weight-bold">
                {{ totalReportQty }}
                <span class="percent-text">100%</span>
              </td>
              <td class="text-right font-weight-bold">
                {{ totalReportSale | currency }}
              </td>
              <td v-if="!hideCost" class="text-right font-weight-bold">
                {{ totalReportCost | currency }}
              </td>
              <td v-if="!hideProfit" class="text-right font-weight-bold">
                {{ totalProfit | currency }}
              </td>
            </tr>
          </template>

          <template v-slot:no-data>
            <div class="empty-state">
              No sales found for this date range.
            </div>
          </template>
        </v-data-table>
      </v-card-text>
    </v-card>
  </div>
</template>

<script>
import saleService from "@/modules/ReportView/service.js";

const Product = () => import("@/modules/ReportView/GlobalReport/Product.vue");

export default {
  name: "category-profit-report",

  components: {
    Product,
  },

  props: [
    "start",
    "reset",
    "end",
    "CustomerId",
    "hideProfit",
    "hideCost",
    "serializedItem",
    "groupBy",
  ],

  data() {
    return {
      isLoading: true,
      sortBy: "name",
      Category: [],
      expanded: [],
      headers: [
        {
          text: "",
          value: "data-table-expand",
          sortable: false,
          width: 60,
        },
        {
          text: "Name",
          align: "left",
          value: "name",
          sortable: true,
        },
        {
          text: "Qty",
          align: "center",
          value: "totalQty",
          sortable: true,
        },
        {
          text: "Sale",
          align: "right",
          value: "totalPrice",
          sortable: true,
        },
        {
          text: "Cost",
          align: "right",
          value: "totalCost",
          sortable: true,
        },
        {
          text: "Profit",
          align: "right",
          value: "totalProfit",
          sortable: true,
        },
      ],
    };
  },

  computed: {
    visibleHeaders() {
      return this.headers.filter((header) => {
        if (this.hideCost && header.value === "totalCost") return false;
        if (this.hideProfit && header.value === "totalProfit") return false;

        return true;
      });
    },

    totalReportQty() {
      return this.Category.reduce(
        (sum, row) => sum + Number(row.totalQty || 0),
        0
      );
    },

    totalReportSale() {
      return this.Category.reduce(
        (sum, row) => sum + Number(row.totalPrice || 0),
        0
      );
    },

    totalReportCost() {
      return this.Category.reduce(
        (sum, row) => sum + Number(row.totalCost || 0),
        0
      );
    },

    totalProfit() {
      return this.totalReportSale - this.totalReportCost;
    },

    exportRows() {
      return this.Category.map((row) => ({
        name: row.name || "N/A",
        totalQty: Number(row.totalQty || 0),
        qtyPercent: this.getQtyPercent(row),
        totalPrice: Number(row.totalPrice || 0),
        totalCost: Number(row.totalCost || 0),
        totalProfit: Number(row.totalProfit || 0),
        profitPercent: this.getProfitPercent(row),
      }));
    },
  },

  watch: {
    groupBy() {
      this.getSaleByCategory();
    },
    reset() {
      this.getSaleByCategory();
    },
    CustomerId() {
      this.getSaleByCategory();
    },
    serializedItem() {
      this.getSaleByCategory();
    },
    start() {
      this.getSaleByCategory();
    },
    end() {
      this.getSaleByCategory();
    },
  },

  async created() {
    await this.getSaleByCategory();
  },

  methods: {
    getQtyPercent(item) {
      if (!this.totalReportQty) return "0.00";

      return ((Number(item.totalQty || 0) / this.totalReportQty) * 100).toFixed(
        2
      );
    },

    getProfitPercent(item) {
      const cost = Number(item.totalCost || 0);
      const profit = Number(item.totalProfit || 0);

      if (!cost) return "0.00";

      return ((profit / cost) * 100).toFixed(2);
    },

    async getSaleByCategory() {
      this.isLoading = true;

      const where = {};

      if (this.start) where.start = this.start;
      if (this.end) where.end = this.end;
      if (this.CustomerId) where.CustomerId = this.CustomerId;
      if (this.serializedItem) where.serializedItem = true;
      if (this.groupBy) where.groupBy = this.groupBy;

      try {
        const response = await saleService.getCustomerPL(where);

        this.Category = response.data || [];

        this.$emit("fatchData", this.totalProfit);
        this.$emit("export-data", this.exportRows);
      } finally {
        this.$Progress.finish();
        this.isLoading = false;
      }
    },
  },
};
</script>

<style scoped>
.category-profit-report {
  width: 100%;
}

.report-table-card {
  border-radius: 16px;
  overflow: hidden;
}

.report-table-header {
  background: #fff;
  padding: 18px 20px;
}

.report-title {
  font-size: 18px;
  font-weight: 700;
  color: #1f2937;
}

.report-subtitle {
  font-size: 13px;
  color: #6b7280;
  margin-top: 3px;
}

.summary-box {
  min-width: 110px;
  padding: 8px 12px;
  margin-left: 8px;
  border: 1px solid #e5e7eb;
  border-radius: 12px;
  text-align: right;
  background: #f9fafb;
}

.summary-label {
  font-size: 11px;
  color: #6b7280;
  text-transform: uppercase;
}

.summary-value {
  font-size: 15px;
  font-weight: 700;
  color: #111827;
}

.summary-value.loss,
.money.loss {
  color: #b91c1c;
}

.item-name {
  font-weight: 600;
  text-transform: capitalize;
  color: #111827;
}

.money {
  font-weight: 700;
}

.percent-text {
  margin-left: 6px;
  font-size: 12px;
  color: #be185d;
}

.profit-percent {
  margin-left: 6px;
  font-size: 12px;
  color: #047857;
}

.expanded-panel {
  padding: 14px;
  background: #f3f8fc;
  border-top: 1px solid #e5e7eb;
}

.total-row {
  background: #f9fafb;
}

.empty-state {
  padding: 32px;
  text-align: center;
  color: #6b7280;
}

.category-profit-table ::v-deep th {
  font-size: 12px;
  font-weight: 700;
  color: #374151 !important;
  background: #f9fafb;
  text-transform: uppercase;
}

.category-profit-table ::v-deep td {
  font-size: 13px;
}

@media print {
  .report-table-card {
    border: none !important;
    border-radius: 0;
  }

  .report-table-header {
    padding: 10px 0;
  }

  .summary-box {
    border: none;
    background: #fff;
  }

  .no-print {
    display: none !important;
  }
}
</style>